import axios from "axios";
import { toast } from "sonner";
import { storage } from "@/lib/storage";
import { compressImage } from "@/lib/image";
import { mapLimit } from "@/lib/pool";
import { dropAfterWrite } from "@/lib/queryClient";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Každý request nese id běžícího sestavení (156) — superadmin pak vidí, kdo jede na staré verzi.
const BUILD = process.env.REACT_APP_BUILD_ID;
api.interceptors.request.use((cfg) => {
  if (BUILD && BUILD !== "dev") cfg.headers["X-App-Build"] = BUILD;
  return cfg;
});

const AUTH_PATHS = ["/login", "/register", "/accept-invite", "/reset-password", "/forgot-password", "/verify-email", "/s/", "/payment", "/kalkulacka", "/demo/"];
const NO_REFRESH = ["/auth/login", "/auth/register", "/auth/refresh", "/auth/forgot-password", "/auth/reset-password", "/auth/accept-invite"];

let refreshing = null;

// 261: Publish v Emergentu restartuje server a Cloudflare pak chvíli vrací 502/52x s anglickou
// hláškou („The origin web server sent a response that Cloudflare could not parse…“). Čtení a
// přihlášení heslem se samy zkusí znovu (~45 s). Krátký výpadek člověk nepozná (jen čeká
// tlačítko); po ~5 s se nahoře ukáže neutrální „Chvilku strpení…“ a teprve když to nevyjde,
// dostane každé místo v aplikaci českou hlášku o aktualizaci místo té z Cloudflaru.
// Ostatní zápisy se znovu neposílají — nevíme jistě, jestli je server nezpracoval. Přihlášení
// jen 3× (~10 s), ať se ani v nejhorším případě nepřiblíží zámku účtu po 8 chybných heslech.
// Čekání má rozptyl ±25 %, aby se po návratu serveru všechny telefony neozvaly v tutéž chvíli.
export const SERVER_RESTART_MSG = "Aplikace se právě aktualizuje — obvykle to trvá do minuty. Zkus to prosím za chvilku znovu.";
const GATEWAY = [502, 503, 504, 520, 521, 522, 523, 524, 525, 526, 527, 530];
const RETRY_WAIT = [1500, 3000, 5000, 8000, 12000, 15000];
const RETRY_POST = ["/auth/login"];
const RETRY_POST_MAX = 3;
const WAIT_TOAST = "pp-server-wait";
function serverDown(err) {
  const r = err?.response;
  if (!r || !GATEWAY.includes(r.status)) return false;
  const d = r.data;
  // naše vlastní 502 (Stripe, úložiště) mají jen `detail` česky — ty se nechávají být
  if (d && typeof d === "object" && !Array.isArray(d)) return !!(d.type || d.title) || d.detail == null;
  return true;   // HTML, text nebo prázdná odpověď z proxy
}

api.interceptors.response.use(
  (res) => { dropAfterWrite(res.config?.method, res.config?.url); return res; },   // 289: Přehled bez starých čísel
  async (error) => {
    const status = error?.response?.status;
    const original = error.config || {};
    const url = original.url || "";
    const onAuthPage = AUTH_PATHS.some((p) => window.location.pathname.startsWith(p));

    if (serverDown(error)) {
      const method = (original.method || "get").toLowerCase();
      const n = original._gw || 0;
      const read = method === "get" || method === "head";
      const max = read ? RETRY_WAIT.length : RETRY_POST.some((p) => url.includes(p)) ? RETRY_POST_MAX : 0;
      if (n < max) {
        original._gw = n + 1;
        if (n === 2) toast.loading("Chvilku strpení…", { id: WAIT_TOAST });   // až po ~5 s čekání
        await new Promise((r) => setTimeout(r, RETRY_WAIT[n] * (0.75 + Math.random() * 0.5)));
        const res = await api(original);
        toast.dismiss(WAIT_TOAST);
        return res;
      }
      toast.dismiss(WAIT_TOAST);
      error.ppServerDown = true;   // react-query už znovu nezkouší (index.js) — čekalo se tu
      error.response.data = { detail: SERVER_RESTART_MSG };
      return Promise.reject(error);
    }

    // Access token expired -> try a one-time silent refresh via the httpOnly refresh cookie.
    if (status === 401 && !original._retry && !NO_REFRESH.some((p) => url.includes(p))) {
      original._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        await refreshing;
        refreshing = null;
        // 296: obnova tokenu k souborům čeká na rozběhnutou žádost o token. Když je tou žádostí
        // právě tenhle požadavek, čekal by sám na sebe — náhledy dokladů a fotek by po 15 min
        // nečinnosti zůstaly prázdné až do obnovení stránky. Žádost o token se jen zopakuje.
        if (!url.includes("/files/token")) await refreshFileToken(true);
        return api(original);
      } catch (e) {
        refreshing = null;
        // 190: odhlásit JEN když server přihlášení opravdu odmítl (401/403). Výpadek
        // signálu na stavbě, timeout nebo restart serveru (5xx) člověka nevyhodí —
        // požadavek selže s hláškou o spojení a obnova se zkusí při dalším.
        const st = e?.response?.status;
        if (st !== 401 && st !== 403) return Promise.reject(e);
        storage.remove("pp-uid");
        if (!onAuthPage) window.location.href = "/login";
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Došlo k chybě. Zkuste to prosím znovu.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;   // např. 409 duplicita (149)
  return String(detail);
}

// Hláška z libovolné axios chyby. Bez tohohle spadne plain-textová 500 ze
// Starlette (bez pole `detail`) na obecné „Došlo k chybě", které nic neřekne.
export function apiError(err) {
  const d = err?.response?.data;
  if (d && d.detail != null) return formatApiError(d.detail);
  if (typeof d === "string" && d.trim()) {
    const t = d.trim();
    // HTML z proxy (Cloudflare 502 apod.) nikdy nepatří do toastu (131/C)
    if (t.startsWith("<") || /cloudflare|<html/i.test(t))
      return `Server je přetížený nebo se restartuje — zkuste to za chvíli znovu. (${err.response.status})`;
    return `${t.slice(0, 160)} (${err.response.status})`;
  }
  if (!err?.response) return "Server neodpověděl — zkontrolujte připojení.";
  return `Chyba serveru (${err.response.status}).`;
}

// ---- Signed short-lived file access tokens (10 min) ----
let fileToken = null;      // short-lived signed token: memory only
let fileTokenAt = 0;       // kdy byl vydán (ms)
let fileTokenInflight = null;   // 216: běžící žádost o token — sdílí ji všichni volající
let fileTokenGen = 0;           // clearFileCache zahodí i rozběhnutou žádost
const FILE_TOKEN_REUSE_MS = 8 * 60 * 1000;  // TTL na serveru je 10 min
const FILE_TOKEN_FORCE_MIN_MS = 5 * 1000;   // vynucená obnova těsně po vydání = už máme čerstvý

// Bez `force` vrátí stávající token, dokud je čerstvý. Je to zásadní pro
// kešování: kdyby se `sig` měnil při každém obrázku, měl by každý obrázek
// jinou URL a prohlížeč by ho nikdy nemohl vzít z cache.
// 216: souběžná volání (mřížka se stovkou dlaždic po návratu z pozadí, nebo
// dlaždice, které všechny selžou na prošlý podpis) sdílí JEDNU žádost o token.
export function refreshFileToken(force = false) {
  const age = Date.now() - fileTokenAt;
  if (fileToken && age < (force ? FILE_TOKEN_FORCE_MIN_MS : FILE_TOKEN_REUSE_MS)) return Promise.resolve(fileToken);
  if (!fileTokenInflight) {
    const gen = fileTokenGen;
    const p = api.get("/files/token")
      .then(({ data }) => {
        if (gen === fileTokenGen) { fileToken = data.token; fileTokenAt = Date.now(); }
        return fileToken;
      })
      .catch(() => fileToken)   /* ignore; will retry on next tick */
      .finally(() => { if (fileTokenInflight === p) fileTokenInflight = null; });
    fileTokenInflight = p;
  }
  return fileTokenInflight;
}

// Smaže lokální cache souborů (service worker). Volá se při odhlášení a při
// přihlášení jiného účtu, ať na zařízení nezůstanou cizí fotky.
export async function clearFileCache() {
  fileToken = null;
  fileTokenAt = 0;
  fileTokenGen += 1;
  fileTokenInflight = null;
  try { await caches.delete("proyekty-files-v1"); } catch { /* SW nemusí být */ }
}

// 216: náhledy obrázků. Server umí `?w=320|640|1280` (kratší strana náhledu,
// vyrobí ho při prvním požadavku a uloží). `variantWidth(cssPx)` vybere nejmenší
// náhled, který na displeji (hustota max. 2×) pokryje prvek velký cssPx; když
// nestačí ani 1280, vrátí null = originál.
export const IMG_VARIANTS = [320, 640, 1280];
export function variantWidth(cssPx) {
  if (!cssPx) return null;
  const dpr = typeof window !== "undefined" ? Math.min(Math.max(window.devicePixelRatio || 1, 1), 2) : 1;
  return IMG_VARIANTS.find((w) => w >= cssPx * dpr) || null;
}

const isPdfUrl = (url) => /\.pdf($|\?)/i.test(url || "");

export function authFileUrl(url, w) {
  const sep = url.includes("?") ? "&" : "?";
  const size = w && !isPdfUrl(url) ? `&w=${w}` : "";
  return `${BACKEND_URL}${url}${sep}sig=${fileToken || ""}${size}`;
}

// Resolve an image URL: internal /api/files paths get a signed token; external URLs pass through.
// `w` (volitelně) = náhled dané šířky — jen u našich souborů, PDF vždy v originále.
export function resolveImg(url, w) {
  if (!url) return null;
  if (url.startsWith("/api/files")) return authFileUrl(url, w);
  return url;
}

// 257 — nahrávání na stavbě (slabá wifi, přepínání wifi ↔ data, zhasnutá obrazovka, iPhone
// i Android). Opakuje se všechno, co je přechodné: žádná odpověď (výpadek, vypršený čas),
// 5xx, 408, 425, 429 a 409 (server ten samý kousek ještě dokončuje). Neopakují se chyby,
// které nové kolo nespraví (400 typ souboru, 401 přihlášení, 413 velikost…). Opakovat je
// bezpečné: každé nahrání nese své `upload_id` a server stejné ID uloží jen jednou.
export function isRetryable(err) {
  const r = err?.response;
  if (!r) return true;                                  // síť spadla / vypršel čas / prohlížeč přerušil
  if (r.status === 404) return typeof r.data === "string";   // „404 page not found" z proxy, když se backend probouzí
  return r.status >= 500 || [408, 409, 425, 429].includes(r.status);
}
const RETRY_WAIT_MS = [1500, 3000, 6000, 12000, 20000];   // ~45 s celkem, pak hláška

// Telefon bez signálu: další pokus až po návratu sítě (nejvýš 60 s), ne naslepo.
function waitOnline(maxMs = 60000) {
  if (typeof navigator === "undefined" || navigator.onLine !== false) return Promise.resolve();
  return new Promise((res) => {
    const done = () => { window.removeEventListener("online", done); clearTimeout(t); res(); };
    const t = setTimeout(done, maxMs);
    window.addEventListener("online", done);
  });
}

async function postRetry(url, data, cfg) {
  let lastErr = null;
  for (let attempt = 0; attempt <= RETRY_WAIT_MS.length; attempt++) {
    if (attempt) {
      const base = RETRY_WAIT_MS[attempt - 1];
      await new Promise((res) => setTimeout(res, base + Math.round(Math.random() * base * 0.3)));
      await waitOnline();
    }
    try {
      return await api.post(url, data, cfg);
    } catch (err) {
      lastErr = err;
      if (!isRetryable(err)) throw err;
    }
  }
  // Ani po šesti pokusech. Do hlášky patří, CO se stalo — jinak se z hlášení
  // uživatele nikdy nepozná, jestli šlo o přerušené spojení, nebo o chybu serveru.
  const st = lastErr?.response?.status;
  const why = st ? `server odpověděl ${st}` : lastErr?.code === "ECONNABORTED" ? "spojení je moc pomalé" : "spojení se serverem se přerušilo";
  const err = new Error("Upload selhal");
  err.response = { status: st || 503, data: { detail:
    `Nahrání se nepovedlo — ${why}. Zkuste to prosím znovu, až bude lepší signál.` } };
  throw err;
}

// Časové limity jednoho požadavku: bez nich na slabé wifi požadavek „visí" donekonečna
// a nahrávání vypadá jako zamrzlé. Kousek 512 kB projde i přes 0,2 Mb/s pod minutu.
const UPLOAD_TIMEOUT_MS = 60000;
const FINISH_TIMEOUT_MS = 90000;

// Proxy na produkci utíná velká request body — všechno nad jeden kousek se
// proto posílá po ~512 kB částech (POST /upload/chunk + /upload/finish).
const CHUNK_BYTES = 512 * 1024;

// `maxPx` = strop delší hrany fotky. 1600 px stačí na doklady i na dokumentaci
// stavby; avatar si říká o míň (viz volání v Settings.js).
export async function uploadFile(file, { maxPx = 1600, quality = 0.7, onProgress } = {}) {
  // Jedno ID na celé nahrání — opakovaný požadavek po ztracené odpovědi nevytvoří druhý soubor.
  const uploadId = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  // Malý soubor v plné velikosti neřešíme — už to je zmenšenina (některá místa
  // volají compressImage sama) a nemá cenu ji překódovávat podruhé.
  let payload = (maxPx >= 1600 && file?.size <= 900000) ? file : await compressImage(file, maxPx, quality);
  // File z <input> je jen ODKAZ na soubor na disku — prohlížeč ho čte až při
  // odeslání. Když mu macOS čtení odmítne (fotka vybraná přes sekci Fotky ve
  // výběru souboru, originál odložený v iCloudu, mezitím smazaný soubor),
  // request umře na net::ERR_ACCESS_DENIED za pár ms, bez HTTP odpovědi
  // a bez použitelné hlášky. Proto soubor načteme do paměti UŽ TEĎ: buď selže
  // tady, kde umíme říct proč, nebo se posílá kopie, které už nic nechybí.
  // Velké fotky tímhle problémem netrpí — jdou přes compressImage, který
  // vrací Blob v paměti; tohle dorovnává malé soubory posílané v originále.
  if (payload === file) {
    try {
      payload = new Blob([await file.arrayBuffer()], { type: file.type });
      payload.name = file.name;
    } catch {
      const e = new Error("FILE_READ");
      e.response = { status: 400, data: { detail:
        "Prohlížeč nemá přístup k souboru. Zkopírujte fotku na plochu a nahrajte kopii" +
        " — a v Nastavení systému → Soukromí a zabezpečení povolte prohlížeči přístup k Fotkám." } };
      throw e;
    }
  }
  // 274: HEIC/HEIF (Samsung, iPhone) se posílá tak, jak je — server ho převede na JPEG.
  const name = payload.name || file?.name || "photo.jpg";
  if (payload.size <= CHUNK_BYTES) {
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("file", payload, name);
    // Content-Type nikdy nesetujeme ručně — axios/prohlížeč si sám doplní
    // `multipart/form-data; boundary=…`. Bez boundary Chrome multipart odmítá
    // a v DevTools se objeví matoucí net::ERR_ACCESS_DENIED, ne 400 ze serveru.
    const { data } = await postRetry("/upload", fd, { timeout: UPLOAD_TIMEOUT_MS });
    return data.url;
  }
  const total = Math.ceil(payload.size / CHUNK_BYTES);
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("index", String(i));
    fd.append("total", String(total));
    fd.append("chunk", payload.slice(i * CHUNK_BYTES, (i + 1) * CHUNK_BYTES));
    await postRetry("/upload/chunk", fd, { timeout: UPLOAD_TIMEOUT_MS });
    // U 60MB výkresu jede přes sto kousků — bez průběhu to vypadá jako zamrznutí.
    onProgress?.(i + 1, total);
  }
  const { data } = await postRetry("/upload/finish", { upload_id: uploadId, filename: name }, { timeout: FINISH_TIMEOUT_MS });
  return data.url;
}

// Obrazovka zůstane během nahrávání rozsvícená (Chrome / Android, Safari 16.4+); kde to
// prohlížeč neumí nebo odmítne, nic se neděje.
async function keepAwake() {
  try { return navigator.wakeLock && document.visibilityState === "visible" ? await navigator.wakeLock.request("screen") : null; }
  catch { return null; }
}

// 216: nahrání víc souborů najednou — nejvýš `concurrency` (257: 2) naráz, takže se
// překryje komprese v telefonu, síť i ukládání na serveru. Pořadí výsledků =
// pořadí souborů; jedno selhání nezahodí ostatní. `prepare(file)` (např.
// compressImage) běží uvnitř téhož slotu. Po každém souboru `onResult(result,
// index)` a `onEach(done, total)`.
// Vrací { urls: [úspěšné URL v pořadí], failed: [{ file, error }], results }.
export async function uploadFiles(files, { concurrency = 2, onEach, onResult, prepare, ...opts } = {}) {
  const list = Array.from(files || []);
  let done = 0;
  const lock = await keepAwake();   // 257: zhasnutá obrazovka by nahrávání uspala
  const results = await mapLimit(list, concurrency, async (file, i) => {
    let res;
    try {
      const payload = prepare ? await prepare(file) : file;
      res = { ok: true, file, url: await uploadFile(payload, opts) };
    } catch (error) {
      res = { ok: false, file, error };
    }
    done += 1;
    try { onResult?.(res, i); onEach?.(done, list.length); } catch { /* UI callback nesmí shodit nahrávání */ }
    return res;
  });
  lock?.release?.().catch?.(() => {});
  return {
    urls: results.filter((r) => r.ok).map((r) => r.url),
    failed: results.filter((r) => !r.ok).map(({ file, error }) => ({ file, error })),
    results,
  };
}

// OCR dokladu běží na backendu jako úloha na pozadí a tady ji krátkými GET
// requesty pollujeme. Díky tomu žádný request nevisí déle než pár set ms, takže
// nenastane Cloudflare 524 ("origin didn't respond") u vícestránkových faktur.
// kind: "document" (faktura) | "receipt" (účtenka) | "sub-invoice" (faktura
// subdodavatele). Vrací načtený objekt dat, chyby vyhazuje se stejným tvarem
// `err.response` jako axios (kvůli volajícím).
const OCR_BASE = { receipt: "/receipts/ocr", "sub-invoice": "/sub-invoices/ocr" };

// Společná pollovací smyčka — POST založí úlohu, GET `${statusBase}/{job_id}`
// hlídá stav. Chování ocrScan se vytažením nesmělo změnit.
async function ocrPoll(postUrl, statusBase, fileUrl) {
  const { data: job } = await api.post(postUrl, { file_url: fileUrl });
  if (!job || !job.job_id) return job; // zpětná kompatibilita, kdyby backend vrátil výsledek přímo
  const deadline = Date.now() + 5 * 60 * 1000; // tvrdý strop ~5 min
  let delay = 1500;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, delay));
    delay = Math.min(delay + 500, 3000);
    const { data: st } = await api.get(`${statusBase}/${job.job_id}`);
    if (st.status === "done") return st.result;
    if (st.status === "error") {
      const err = new Error(st.detail || "OCR selhalo");
      err.response = { status: 422, data: { detail: st.detail } };
      throw err;
    }
  }
  const err = new Error("OCR timeout");
  err.response = { status: 504, data: { detail: "Načítání dokladu trvá příliš dlouho. Zkuste to prosím znovu." } };
  throw err;
}

export async function ocrScan(kind, fileUrl) {
  const base = OCR_BASE[kind] || "/documents/ocr";
  return ocrPoll(base, base, fileUrl);
}

export async function ocrScanCase(caseId, fileUrl) {
  return ocrPoll(`/cashbox/my-cases/${caseId}/ocr`, `/cashbox/my-cases/ocr`, fileUrl);
}

// Trigger a browser download of a protected export via a fresh signed short-lived URL (no Bearer in query).
export async function downloadSigned(path, filename) {
  await refreshFileToken(true);
  const sep = path.includes("?") ? "&" : "?";
  const url = `${BACKEND_URL}${path}${sep}sig=${fileToken || ""}`;
  const a = document.createElement("a");
  a.href = url;
  if (filename) a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}
